/**
 * Colour grade parameter model + post-process shader (issue #59): AgX tone
 * mapping (set on the renderer in main.ts) plus a teal-orange split-tone
 * grade applied as a single full-screen post pass. Grade keyframe shape
 * (white balance / shadow tint+amount / highlight tint+amount / saturation /
 * contrast) adapted from Braffolk/fable5-world-demo `ColorScript.ts` (MIT
 * licence, commit fd75fdb).
 *
 * Everything in this module is pure, three.js-free maths so it is
 * unit-tested directly in Node (mirrors the RouteRibbon/BuildingsLayer split
 * between pure geometry/param builders and the three.js wrapper). The scene
 * has a single fixed time-of-day today, so `DEFAULT_GRADE` is the only
 * keyframe main.ts uses, but `interpolateGrade` keeps the shape general so a
 * future day/night cycle can drive the grade across multiple keyframes
 * without changing this module.
 */

export interface RGB {
  r: number;
  g: number;
  b: number;
}

export interface GradeParams {
  /** Multiplicative white-balance correction; 1,1,1 is neutral. */
  whiteBalance: RGB;
  /** Colour pushed into shadows (cool teal). */
  shadowTint: RGB;
  /** 0..1 strength of the shadow tint. */
  shadowAmount: number;
  /** Colour pushed into highlights (warm orange). */
  highlightTint: RGB;
  /** 0..1 strength of the highlight tint. */
  highlightAmount: number;
  /** 1 = neutral saturation, >1 boosts, <1 desaturates. */
  saturation: number;
  /** 1 = neutral contrast, >1 increases contrast around mid-grey. */
  contrast: number;
  /** 0..1 strength of corner darkening (issue #64); at 1.0 the corners go
   * fully black, so authored values stay well below that (see DEFAULT_GRADE). */
  vignetteStrength: number;
  /** 0..1 strength of static per-pixel film grain (issue #64); authored
   * values stay tiny so grain is present but imperceptible in stills. */
  grainAmount: number;
}

/** A grade at a normalised time position (e.g. 0..1 across a day cycle). */
export interface GradeKeyframe {
  t: number;
  params: GradeParams;
}

const WHITE_BALANCE_MIN = 0;
const WHITE_BALANCE_MAX = 4;
const AMOUNT_MIN = 0;
const AMOUNT_MAX = 1;
const SATURATION_MIN = 0;
const SATURATION_MAX = 2;
const CONTRAST_MIN = 0.5;
const CONTRAST_MAX = 2;
const VIGNETTE_MIN = 0;
const VIGNETTE_MAX = 1;
// Grain is a small additive dither around each pixel's colour; anything above
// this ceiling stops reading as "grain" and starts reading as visible noise.
const GRAIN_MIN = 0;
const GRAIN_MAX = 0.25;

function clampRange(value: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, value));
}

function clamp01(value: number): number {
  return clampRange(value, 0, 1);
}

function clampRGB(c: RGB, lo: number, hi: number): RGB {
  return {
    r: clampRange(c.r, lo, hi),
    g: clampRange(c.g, lo, hi),
    b: clampRange(c.b, lo, hi),
  };
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpRGB(a: RGB, b: RGB, t: number): RGB {
  return { r: lerp(a.r, b.r, t), g: lerp(a.g, b.g, t), b: lerp(a.b, b.b, t) };
}

/** Clamps every field of a grade to its valid range (defends against a
 * future day-cycle keyframe or authoring mistake landing out of bounds). */
export function clampGradeParams(p: GradeParams): GradeParams {
  return {
    whiteBalance: clampRGB(p.whiteBalance, WHITE_BALANCE_MIN, WHITE_BALANCE_MAX),
    shadowTint: clampRGB(p.shadowTint, WHITE_BALANCE_MIN, WHITE_BALANCE_MAX),
    shadowAmount: clampRange(p.shadowAmount, AMOUNT_MIN, AMOUNT_MAX),
    highlightTint: clampRGB(p.highlightTint, WHITE_BALANCE_MIN, WHITE_BALANCE_MAX),
    highlightAmount: clampRange(p.highlightAmount, AMOUNT_MIN, AMOUNT_MAX),
    saturation: clampRange(p.saturation, SATURATION_MIN, SATURATION_MAX),
    contrast: clampRange(p.contrast, CONTRAST_MIN, CONTRAST_MAX),
    vignetteStrength: clampRange(p.vignetteStrength, VIGNETTE_MIN, VIGNETTE_MAX),
    grainAmount: clampRange(p.grainAmount, GRAIN_MIN, GRAIN_MAX),
  };
}

/** Linearly interpolates between two (already-valid) grades and clamps the
 * result, so an out-of-range `t` still yields a usable grade. */
export function lerpGradeParams(a: GradeParams, b: GradeParams, t: number): GradeParams {
  const ct = clamp01(t);
  return clampGradeParams({
    whiteBalance: lerpRGB(a.whiteBalance, b.whiteBalance, ct),
    shadowTint: lerpRGB(a.shadowTint, b.shadowTint, ct),
    shadowAmount: lerp(a.shadowAmount, b.shadowAmount, ct),
    highlightTint: lerpRGB(a.highlightTint, b.highlightTint, ct),
    highlightAmount: lerp(a.highlightAmount, b.highlightAmount, ct),
    saturation: lerp(a.saturation, b.saturation, ct),
    contrast: lerp(a.contrast, b.contrast, ct),
    vignetteStrength: lerp(a.vignetteStrength, b.vignetteStrength, ct),
    grainAmount: lerp(a.grainAmount, b.grainAmount, ct),
  });
}

/**
 * Resolves the grade at time `t` across a sorted list of keyframes (by
 * ascending `t`). Holds the first/last keyframe's params outside the
 * keyframe range rather than extrapolating.
 */
export function interpolateGrade(keyframes: GradeKeyframe[], t: number): GradeParams {
  if (keyframes.length === 0) {
    throw new Error('interpolateGrade requires at least one keyframe');
  }
  const first = keyframes[0];
  const last = keyframes[keyframes.length - 1];
  if (keyframes.length === 1 || t <= first.t) return clampGradeParams(first.params);
  if (t >= last.t) return clampGradeParams(last.params);

  for (let i = 1; i < keyframes.length; i++) {
    const upper = keyframes[i];
    if (t <= upper.t) {
      const lowerBound = keyframes[i - 1];
      const span = upper.t - lowerBound.t;
      const localT = span > 0 ? (t - lowerBound.t) / span : 0;
      return lerpGradeParams(lowerBound.params, upper.params, localT);
    }
  }
  return clampGradeParams(last.params);
}

/**
 * The single tuned keyframe for the viewer's fixed time-of-day: cool teal
 * shadows, warm orange highlights, saturation/contrast pushed above neutral.
 */
export const DEFAULT_GRADE: GradeParams = {
  whiteBalance: { r: 1.0, g: 1.0, b: 1.0 },
  shadowTint: { r: 0.85, g: 0.93, b: 1.06 },
  shadowAmount: 0.35,
  // Highlight warm push softened (issue #90): the original {1.08, 1.0, 0.88}
  // pushed red into every lit surface, which measurably compounded the brown
  // terrain reading on the sun-warmed uplands (the whole point of #90). Kept
  // gently warm for the teal-orange character, but no longer red-biased enough
  // to flip lit ground from green to brown — verified in the terrainPalette
  // e2e with the grade both on and off.
  highlightTint: { r: 1.03, g: 1.0, b: 0.94 },
  highlightAmount: 0.3,
  saturation: 1.12,
  contrast: 1.08,
  // Corner darkening tops out at ~18% (issue #64 AC: "≤~20%"); grain is a
  // tiny ±0.015 dither (0.03 amount / 2), present but imperceptible in stills.
  vignetteStrength: 0.18,
  grainAmount: 0.03,
};

/** Builds a `ShaderPass`-compatible uniforms object from a grade. `postEnabled`
 * gates only the issue #64 additions (vignette + grain), independent of
 * `?grade=off` which disables the whole pass including the #59 tint/contrast
 * work — this lets `?post=off` A/B just the new polish on top of the grade. */
export function gradeUniforms(
  params: GradeParams,
  postEnabled = true,
): Record<string, { value: unknown }> {
  const p = clampGradeParams(params);
  return {
    // ShaderPass only binds the composer's read buffer to its textureID
    // ('tDiffuse') when the key already exists in the uniforms object;
    // without this slot the pass samples an unbound texture and outputs a
    // black frame.
    tDiffuse: { value: null },
    uWhiteBalance: { value: [p.whiteBalance.r, p.whiteBalance.g, p.whiteBalance.b] },
    uShadowTint: { value: [p.shadowTint.r, p.shadowTint.g, p.shadowTint.b] },
    uShadowAmount: { value: p.shadowAmount },
    uHighlightTint: { value: [p.highlightTint.r, p.highlightTint.g, p.highlightTint.b] },
    uHighlightAmount: { value: p.highlightAmount },
    uSaturation: { value: p.saturation },
    uContrast: { value: p.contrast },
    uVignetteStrength: { value: p.vignetteStrength },
    uGrainAmount: { value: p.grainAmount },
    uPostEnabled: { value: postEnabled ? 1 : 0 },
  };
}

/** Reads `?grade=off` from a location search string; any other value (or its
 * absence) leaves the grade enabled. Mirrors `parseQualityOverride`'s shape
 * in `core/quality.ts`. */
export function parseGradeOverride(search: string): boolean {
  return new URLSearchParams(search).get('grade') !== 'off';
}

/** Reads `?post=off` from a location search string (issue #64): disables only
 * the bloom/vignette/grain polish for an A/B screenshot, independent of
 * `?grade=off`'s wholesale grade bypass. */
export function parsePostOverride(search: string): boolean {
  return new URLSearchParams(search).get('post') !== 'off';
}

export const GRADE_VERTEX_SHADER = `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

/** White-balance multiply -> luminance-keyed shadow/highlight split-tone ->
 * saturation -> contrast (issue #59), then vignette -> static grain (issue
 * #64), in that order — the vignette/grain step is the final "film" pass so
 * it applies to the fully graded image. Both are gated by `uPostEnabled` so
 * `?post=off` can A/B them independently of `?grade=off`. */
export const GRADE_FRAGMENT_SHADER = `
uniform sampler2D tDiffuse;
uniform vec3 uWhiteBalance;
uniform vec3 uShadowTint;
uniform float uShadowAmount;
uniform vec3 uHighlightTint;
uniform float uHighlightAmount;
uniform float uSaturation;
uniform float uContrast;
uniform float uVignetteStrength;
uniform float uGrainAmount;
uniform float uPostEnabled;
varying vec2 vUv;

// Named gradeLuma, not luminance: three.js injects its own
// luminance( const in vec3 ) into every fragment shader prefix
// (WebGLProgram), so redefining that name fails to compile.
float gradeLuma(vec3 c) {
  return dot(c, vec3(0.2126, 0.7152, 0.0722));
}

// Cheap per-pixel hash noise (no time uniform): fixed to screen position so
// the grain is static rather than shimmering frame-to-frame.
float grainHash(vec2 coord) {
  return fract(sin(dot(coord, vec2(12.9898, 78.233))) * 43758.5453);
}

void main() {
  vec4 texel = texture2D(tDiffuse, vUv);
  vec3 color = texel.rgb * uWhiteBalance;

  float lum = gradeLuma(color);
  vec3 shadowed = mix(color, color * uShadowTint, uShadowAmount * (1.0 - lum));
  vec3 split = mix(shadowed, shadowed * uHighlightTint, uHighlightAmount * lum);

  float g = gradeLuma(split);
  vec3 saturated = mix(vec3(g), split, uSaturation);

  vec3 contrasted = (saturated - 0.5) * uContrast + 0.5;

  // Vignette: 0 at centre, 1 at the corners of a 0..1 UV square.
  vec2 centered = vUv - 0.5;
  float falloff = dot(centered, centered) * 2.0;
  float vignette = 1.0 - falloff * uVignetteStrength * uPostEnabled;
  vec3 vignetted = contrasted * clamp(vignette, 0.0, 1.0);

  float grain = (grainHash(gl_FragCoord.xy) - 0.5) * uGrainAmount * uPostEnabled;
  vec3 grained = vignetted + grain;

  gl_FragColor = vec4(clamp(grained, 0.0, 1.0), texel.a);
}
`;
